import {Database, supabase} from '@tnc/shared';
import clsx from 'clsx';
import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {speculatorT} from '@/lib/types/speculator.types';

import {createDataTableConfig} from '@/lib/helpers/config';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {Modal, ModalRefT} from '@/components/Modal';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';
import Icon from '@/components/icon';

type ApplicationWithSpeculatorT = Database['public']['Tables']['Applications']['Row'] & {Speculator: speculatorT};

/**
 * Admin Applications page
 * This page displays all of the applications in the system and allows the admin to view, edit, and delete them
 * @return {JSX.Element} - Admin Applications page
 */
function AdminApplications() {
  // Hooks
  const {isAdmin} = useAuth();
  const {toast} = useNotifications();

  // State
  const [selectedApplication, setSelectedApplication] = useState<ApplicationWithSpeculatorT | null>(null);

  // Refs
  const modalRef = useRef<ModalRefT>(null);

  // Data Table -----------------------------------------------------
  const dataTableConfig = createDataTableConfig({
    title: 'Applications',
    table: 'Applications',
    hideAdd: true,
    columns: ['*', ['Speculator', ['*']]],
    actionsDisplay: [
      {
        icon: 'bi bi-arrows-angle-expand',
        color: 'info',
        handler: row => {
          setSelectedApplication(row);
          modalRef.current?.show();
        },
        permission: () => isAdmin,
      },
    ],
    columnsDisplay: [
      {
        header: 'Application ID',
        field: 'application_id',
      },
      {
        header: 'Status',
        field: 'application_status',
        render: row => (
          <span
            className={clsx(`badge capitalize text-white`, {
              'bg-success': row.application_status === 'approved',
              'bg-danger': row.application_status === 'rejected',
              'bg-warning !text-stone-700': row.application_status === 'pending',
            })}
          >
            {row.application_status}
          </span>
        ),
      },
      {
        header: 'Entity Type',
        field: 'application_entity_type',
        render: row => <span className="capitalize">{row.application_entity_type}</span>,
      },
      {
        header: 'Entity ID',
        field: 'application_entity_id',
        render: row => (
          <div className="flex items-center gap-2">
            <span>{row.application_entity_id}</span>
            <Link
              to={`/${row.application_entity_type}s/${row.application_entity_id}`}
              className="text-primary underline"
              target="_blank"
              rel="noreferrer"
            >
              View
            </Link>
            <div className="flex-1">
              <Icon name="arrow-up-right-square" size={4} />
            </div>
          </div>
        ),
      },
      {
        header: 'Speculator',
        field: 'Speculator.speculator_email',
        render: row => (
          <div className="flex items-center gap-2">
            <Link
              to={`/admin/speculators/${row.Speculator?.speculator_id}`}
              className="text-primary underline"
              target="_blank"
              rel="noreferrer"
            >
              {row.Speculator?.speculator_email}
            </Link>
            <div className="shrink">
              <Icon name="arrow-up-right-square" size={4} />
            </div>
          </div>
        ),
      },
      {
        header: 'Notes',
        field: 'application_notes',
        render: row => (
          <div className="flex flex-col gap-2">
            <span>{row.application_notes}</span>
          </div>
        ),
      },
    ],
  });

  // Function to handle approval
  const handleApprove = async () => {
    if (selectedApplication) {
      try {
        const {data: userData} = await supabase.auth.getUser();

        if (!userData?.user?.id) {
          throw new Error('User not found');
        }

        const {error: applicationError} = await supabase
          .from('Applications')
          .update({application_status: 'approved'})
          .eq('application_id', selectedApplication.application_id)
          .select();

        if (applicationError) {
          throw applicationError;
        }

        const {error: roleError} = await supabase.from('Role').insert({
          role_entity_id: selectedApplication.application_entity_id,
          role_entity_type:
            selectedApplication.application_entity_type as Database['public']['Enums']['role_entity_type'],
          fk_speculator_id: selectedApplication.fk_speculator_id,
          created_by: userData?.user?.id,
          date_created: new Date().toISOString(),
          date_modified: new Date().toISOString(),
        });

        if (roleError) {
          throw roleError;
        }
        toast.success('Application approved');
      } catch (error) {
        console.error('Error approving application:', error);
        toast.error('Error approving application');
      }
    }
  };

  // Function to handle denial
  const handleDeny = async () => {
    if (selectedApplication) {
      try {
        const {error: applicationError} = await supabase
          .from('Applications')
          .update({application_status: 'rejected'})
          .eq('application_id', selectedApplication.application_id)
          .select();

        if (applicationError) {
          throw applicationError;
        }

        toast.success('Application denied');
        modalRef.current?.hide();
      } catch (error) {
        console.error('Error denying application:', error);
        toast.error('Error denying application');
      }
    }
  };

  return (
    <Page currentPage="Applications">
      <DataTable {...dataTableConfig} />
      <Modal ref={modalRef} title="Application Details" size="lg">
        {selectedApplication && (
          <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-col gap-2">
              <span>
                <strong>ID:</strong> {selectedApplication.application_id}
              </span>
              <span className="capitalize">
                <strong>Status:</strong> {selectedApplication.application_status}
              </span>
              <span className="flex grow items-center gap-2">
                <strong>Speculator Email:</strong> {selectedApplication.Speculator?.speculator_email} |{' '}
                <Link
                  to={`/admin/speculators/${selectedApplication.fk_speculator_id}`}
                  className="flex items-center gap-2 text-primary underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  View Speculator
                </Link>
                <div className="flex-1">
                  <Icon name="arrow-up-right-square" size={4} />
                </div>
              </span>
              <span>
                <strong>Entity Type:</strong> {selectedApplication.application_entity_type}
              </span>
              <span className="flex grow items-center gap-2">
                <strong>Entity ID:</strong> {selectedApplication.application_entity_id} |{' '}
                <Link
                  to={`/${selectedApplication.application_entity_type}s/${selectedApplication.application_entity_id}`}
                  className="text-primary underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  View Entity
                </Link>
                <div className="flex-1">
                  <Icon name="arrow-up-right-square" size={4} />
                </div>
              </span>
              <span>
                <strong>Notes:</strong> {selectedApplication.application_notes}
              </span>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="ti-btn ti-btn-danger bg-danger !font-medium text-white" onClick={handleDeny}>
                Deny
              </button>
              <button className="ti-btn ti-btn-primary bg-primary !font-medium text-white" onClick={handleApprove}>
                Approve
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Page>
  );
}

export default AdminApplications;
